import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { positionsService } from "../services/positions.service";
import type { CreatePositionInput, UpdatePositionInput } from "../types";

/**
 * `GET /positions` sahifalanmagan — `success()` butun massivni qaytaradi
 * (`paginated()` emas), shuning uchun `usePaginatedQuery`/`DataTable` o'rniga
 * oddiy `useQuery` va antd jadvalning mijoz tomonidagi sahifalashi ishlatiladi.
 */
export function usePositionsList() {
	return useQuery({
		queryKey: ["positions"],
		queryFn: () => positionsService.list(),
	});
}

function usePositionsMutation<TVariables>(
	mutationFn: (variables: TVariables) => Promise<unknown>,
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["positions"] });
			message.success(successMessage);
		},
		onError: (error) => {
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useCreatePosition() {
	const { t } = useTranslation("positions");
	return usePositionsMutation(
		(input: CreatePositionInput) => positionsService.create(input),
		t("messages.created"),
		t("messages.createFailed")
	);
}

export function useUpdatePosition() {
	const { t } = useTranslation("positions");
	return usePositionsMutation(
		({ id, input }: { id: string; input: UpdatePositionInput }) =>
			positionsService.update(id, input),
		t("messages.updated"),
		t("messages.updateFailed")
	);
}

export function useDeletePosition() {
	const { t } = useTranslation("positions");
	return usePositionsMutation(
		(id: string) => positionsService.remove(id),
		t("messages.deleted"),
		t("messages.actionFailed")
	);
}
